import { create } from "zustand";
import { apiFetch } from "@/lib/api";

export interface SubscriptionPlan {
  id: number;
  channelId: string;
  name: string;
  nameAmharic: string | null;
  description: string | null;
  price: number;
  currency: string;
  duration: "weekly" | "monthly" | "quarterly" | "yearly";
  durationDays: number;
  subscriberCount: number;
  isActive: boolean;
}

export interface Subscriber {
  id: number;
  telegramId: string;
  name: string;
  planName: string;
  status: "active" | "expired" | "grace" | "cancelled";
  startDate: string;
  endDate: string;
  daysLeft: number;
}

export type SubscriberStatus = Subscriber["status"];
export type PlanDuration = SubscriptionPlan["duration"];

interface SubscriptionStats {
  activeSubscribers: number;
  totalRevenue: number;
  expiringThisWeek: number;
  churnRate: number;
}

interface SubscriptionState {
  plans: SubscriptionPlan[];
  subscribers: Subscriber[];
  stats: SubscriptionStats;
  isLoading: boolean;
  error: string | null;
}

interface SubscriptionActions {
  fetchPlans: () => Promise<void>;
  fetchSubscribers: (channelId: string) => Promise<void>;
  addPlan: (plan: CreateSubscriptionPlanInput) => Promise<void>;
  togglePlan: (planId: number) => Promise<void>;
  clearError: () => void;
}

interface ApiSubscriptionPlan extends Omit<SubscriptionPlan, "subscriberCount"> {
  userId: number;
  createdAt: string;
}

interface ApiSubscriber {
  id: number;
  subscriberTelegramId: string;
  planId: number;
  channelId: string;
  status: Subscriber["status"];
  startDate: string;
  endDate: string;
}

interface CreateSubscriptionPlanInput {
  channelId: string;
  name: string;
  nameAmharic?: string;
  description?: string;
  price: number;
  currency?: string;
  duration: PlanDuration;
}

function normalizePlan(plan: ApiSubscriptionPlan): SubscriptionPlan {
  return { ...plan, subscriberCount: 0 };
}

function normalizeSubscriber(
  subscriber: ApiSubscriber,
  plans: SubscriptionPlan[],
): Subscriber {
  const endDate = new Date(subscriber.endDate);
  const daysLeft = Math.max(
    0,
    Math.ceil((endDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000)),
  );
  return {
    id: subscriber.id,
    telegramId: subscriber.subscriberTelegramId,
    name: `Telegram ${subscriber.subscriberTelegramId}`,
    planName: plans.find((plan) => plan.id === subscriber.planId)?.name ?? `Plan ${subscriber.planId}`,
    status: subscriber.status,
    startDate: subscriber.startDate,
    endDate: subscriber.endDate,
    daysLeft,
  };
}

function computeStats(
  plans: SubscriptionPlan[],
  subscribers: Subscriber[],
): SubscriptionStats {
  const active = subscribers.filter((s) => s.status === "active").length;
  const expiring = subscribers.filter(
    (s) => s.status === "active" && s.daysLeft <= 7,
  ).length;
  const total = subscribers.length;
  const churned = subscribers.filter(
    (s) => s.status === "expired" || s.status === "cancelled",
  ).length;
  const churnRate = total > 0 ? Math.round((churned / total) * 100) : 0;
  const totalRevenue = plans.reduce(
    (sum, p) => sum + p.price * p.subscriberCount,
    0,
  );

  return { activeSubscribers: active, totalRevenue, expiringThisWeek: expiring, churnRate };
}

const EMPTY_STATS: SubscriptionStats = {
  activeSubscribers: 0,
  totalRevenue: 0,
  expiringThisWeek: 0,
  churnRate: 0,
};

export const useSubscriptionStore = create<SubscriptionState & SubscriptionActions>(
  (set, get) => ({
    plans: [],
    subscribers: [],
    stats: EMPTY_STATS,
    isLoading: false,
    error: null,

    fetchPlans: async () => {
      set({ isLoading: true, error: null });

      try {
        const response = await apiFetch<{ plans: ApiSubscriptionPlan[] }>(
          "/api/subscriptions/plans",
        );
        const plans = response.plans.map(normalizePlan);
        const subscribers = get().subscribers;
        const stats = computeStats(plans, subscribers);
        set({ plans, stats, isLoading: false });
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load subscription plans";
        set({ plans: [], stats: EMPTY_STATS, error: message, isLoading: false });
      }
    },

    fetchSubscribers: async (channelId) => {
      set({ isLoading: true, error: null });

      try {
        const response = await apiFetch<{
          subscriptions: ApiSubscriber[];
          total: number;
        }>(
          `/api/subscriptions/subscribers?channelId=${encodeURIComponent(channelId)}`,
        );
        const plans = get().plans;
        const subscribers = response.subscriptions.map((subscriber) =>
          normalizeSubscriber(subscriber, plans),
        );
        const stats = computeStats(plans, subscribers);
        set({ subscribers, stats, isLoading: false });
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load subscribers";
        set({ subscribers: [], error: message, isLoading: false });
      }
    },

    addPlan: async (planData) => {
      set({ isLoading: true, error: null });

      try {
        const created = await apiFetch<ApiSubscriptionPlan>("/api/subscriptions/plans", {
          method: "POST",
          body: planData,
        });
        const newPlan = normalizePlan(created);
        const plans = [...get().plans, newPlan];
        const stats = computeStats(plans, get().subscribers);
        set({ plans, stats, isLoading: false });
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to create subscription plan";
        set({ error: message, isLoading: false });
        throw err;
      }
    },

    togglePlan: async (planId) => {
      try {
        const existing = get().plans.find((plan) => plan.id === planId);
        if (!existing) return;
        const updated = await apiFetch<ApiSubscriptionPlan>(
          `/api/subscriptions/plans/${planId}/toggle`,
          { method: "PUT", body: { isActive: !existing.isActive } },
        );
        const updatedPlan = { ...normalizePlan(updated), subscriberCount: existing.subscriberCount };
        const plans = get().plans.map((plan) =>
          plan.id === planId ? updatedPlan : plan,
        );
        set({ plans });
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to toggle plan status";
        set({ error: message });
      }
    },

    clearError: () => set({ error: null }),
  }),
);
